import dayjs from 'dayjs'

import { SuccessToast, WarnToast } from '@/components/Toasts'
// import { Toast } from 'vant'
import { DateType, DateTypeBase } from '@/utils/date'

import { ApiResult, AxiosResponse } from './core'

/**
 * 时间值转为时间戳
 * @param data 请求参数，对应key的内容会被改变
 * @param key 需要求改的字段名
 * @returns 秒时间戳
 */
export function timeToStamp<T extends Record<string, DateType | any>> (data?: T, key?: keyof T) {
  if (data && key && key in data && !!data[key]) {
    (data[key] as DateType) = dayjs(data[key]).unix()
  }

  return data
}

/**
 * 修改data中所有的时间字段
 * @param data 请求参数，对应keys的内容会被改变
 * @param keys 时间字段名
 */
export function timesToStamp<T extends Record<string, any>> (data?: T, keys: Array<keyof T> = ['start_time', 'end_time']) {
  if (data && keys) {
    for (const key of keys) {
      timeToStamp(data, key)
    }
  }
  return data
}

/**
 * 数组转为字符串
 * @param data 请求参数，对应key的内容会被改变
 * @param key 需要求改的字段名
 * @param separator 分隔符
 */
export function joinArray<T extends Record<string, any>> (data?: T, key?: keyof T, separator = ',') {
  if (data && key && key in data) {
    const value = data[key]
    if (Array.isArray(value)) {
      data[key] = value.join(separator)
    }
  }
  return data
}

/**
 * 分割字符串为数组
 * @param data 请求参数，对应key的内容会被改变
 * @param key 需要求改的字段名
 * @param separator 分隔符
 */
export function splitArray<T extends Record<string, any>> (data?: T, key?: keyof T, separator = ',') {
  if (data && key && key in data) {
    const value = data[key]
    if (typeof value === 'string') {
      data[key] = value.split(separator)
    }
  }
  return data
}

/**
 * 设置日期范围的时间
 * @param data 请求参数，对应key的内容会被改变
 * @param begin 起始时间
 * @param end 结束时间
 */
export function setRangeDateTime<T extends Record<string, DateTypeBase | any>> (data?: T, begin: keyof T = 'start_time', end: keyof T = 'end_time') {
  if (!data) return data

  if (begin && (begin in data) && data[begin]) {
    const b = new Date(data[begin] as DateTypeBase)
    b.setHours(0, 0, 0, 0)
    data[begin] = b as any
  }

  if (end && (end in data) && data[end]) {
    const e = new Date(data[end] as DateTypeBase)
    e.setHours(23, 59, 59)

    data[end] = e as any
  }

  return data
}

/**
 * 自动提示接口返回消息
 * @param data 请求返回的data，或请求promise
 * @param successText 成功提示文案
 * @param watingMessageHide 等待提示文案消失
 * @param duration 显示时间
 * @example autoMessage(api.xxx(), '成功').then((data) => { ...code })
 *
 * @example autoMessage((await api.xxx()).data, '成功').then((data) => { ...code })
 *
 * @examole autoMessage(api.xxx().then((res) => { if(res.data.error_code === 5){ res.data.error_code = 0 }; return res }), '成功')
 */
export async function autoMessage<R extends ApiResult> (responseData: R | Promise<AxiosResponse<R>>, successText?: string, watingMessageHide = false, duration = 1500) {
  const data: R = responseData instanceof Promise ? (await responseData).data : responseData

  if (data.error_code === 0) {
    await showMessage(successText, 'success', watingMessageHide, duration)
    return Promise.resolve(data)
  }

  if (data.error_code) {
    await showMessage(data.error_message, 'fail', watingMessageHide, duration)
  }

  // 其他情况直接抛出异常，使用抛出异常会停止执行后面代码的特性，可以阻止后面的代码执行
  // 如果希望继续执行，需要处理异常：.catch((data) => data)
  return Promise.reject(data)
}

async function showMessage (message?: string, type: 'success' | 'fail' = 'success', watingHidden = false, duration = 1500) {
  if (!message) return

  if (watingHidden) {
    await new Promise<void>((resolve) => {
      const toastFN = type === 'success' ? SuccessToast : WarnToast

      const msg = message || ''

      toastFN(msg, duration, () => { resolve() })

      // Toast[type]({
      //   type: type,
      //   message,
      //   duration,
      //   onClose: () => { resolve() },
      // })
    })
  } else {
    // Toast[type]({ message, duration })

    const toastFN = type === 'success' ? SuccessToast : WarnToast

    const msg = message || ''

    toastFN(msg, duration)
  }
}
